import api from './api';
import { DealDTO, DealReceiptInfoDTO, CreateDealRequest } from '../types';
import { API_ENDPOINTS } from '../constants/api';

class DealService {
  // Получение списка сделок
  async getDeals(): Promise<DealDTO[]> {
    try {
      const response = await api.get(API_ENDPOINTS.DEAL.LIST);
      return response.data as DealDTO[];
    } catch (error) {
      console.error('Error fetching deals:', error);
      return [];
    }
  }

  // Получение сделки по ID
  async getDealById(dealId: string): Promise<DealDTO | null> {
    try {
      const response = await api.get(`${API_ENDPOINTS.DEAL.GET_BY_ID}/${dealId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get deal by ID:', error);
      return null;
    }
  }

  async payDeal(dealId: string, receiptEmail: string): Promise<{ url?: string; message?: string } | null> {
    try {
      const response = await api.post(`${API_ENDPOINTS.DEAL.PAY}/${dealId}/pay`, {
        receiptEmail
      });
      return response.data;
    } catch (error) {
      console.error('Failed to pay deal:', error);
      return null;
    }
  }

  async refundDeal(dealId: string): Promise<boolean> {
    try {
      const response = await api.post(`${API_ENDPOINTS.DEAL.REFUND}/${dealId}/refund`);
      return response.status === 204;
    } catch (error) {
      console.error('Failed to refund deal:', error);
      return false;
    }
  }

  async confirmDeal(dealId: string): Promise<boolean> {
    try {
      const response = await api.post(`${API_ENDPOINTS.DEAL.CONFIRM}/${dealId}/confirm`);
      return response.status === 200 || response.status === 204;
    } catch (error) {
      console.error('Failed to confirm deal:', error);
      return false;
    }
  }

  async getDealReceiptInfo(dealId: string, token?: string | null): Promise<DealReceiptInfoDTO | null> {
    try {
      const url = token
        ? `${API_ENDPOINTS.DEAL.RECEIPT}/${dealId}/receipt?token=${encodeURIComponent(token)}`
        : `${API_ENDPOINTS.DEAL.RECEIPT}/${dealId}/receipt`;
      const response = await api.get(url);
      return response.data as DealReceiptInfoDTO;
    } catch (error) {
      console.error('Failed to get receipt info:', error);
      return null;
    }
  }

  async payerConfirmDeal(dealId: string, token: string): Promise<DealDTO | null> {
    try {
      const response = await api.post(
        `${API_ENDPOINTS.DEAL.PAYER_CONFIRM}/${dealId}/payer-confirm?token=${encodeURIComponent(token)}`
      );
      return response.data as DealDTO;
    } catch (error) {
      console.error('Failed to confirm deal as payer:', error);
      return null;
    }
  }

  async payerRejectDeal(dealId: string, token: string): Promise<DealDTO | null> {
    try {
      const response = await api.post(
        `${API_ENDPOINTS.DEAL.PAYER_REJECT}/${dealId}/payer-reject?token=${encodeURIComponent(token)}`
      );
      return response.data as DealDTO;
    } catch (error) {
      console.error('Failed to reject deal as payer:', error);
      return null;
    }
  }

  async sendPayerConfirmEmail(dealId: string): Promise<boolean> {
    try {
      await api.post(`${API_ENDPOINTS.DEAL.SEND_PAYER_EMAIL}/${dealId}/send-payer-confirm-email`);
      return true;
    } catch (error) {
      console.error('Failed to send payer confirm email:', error);
      return false;
    }
  }

  /** Отправить/повторно отправить письмо покупателю со ссылками (при открытии страницы чека). Без авторизации. */
  async ensurePayerEmailSent(dealId: string): Promise<void> {
    try {
      await api.post(`${API_ENDPOINTS.DEAL.RECEIPT}/${dealId}/ensure-payer-email`);
    } catch (error) {
      console.error('Failed to ensure payer email:', error);
    }
  }

  // Создание новой сделки
  async createDeal(dealData: CreateDealRequest): Promise<DealDTO | null> {
    try {
      const response = await api.post(API_ENDPOINTS.DEAL.CREATE, dealData);
      return response.data as DealDTO;
    } catch (error) {
      console.error('Error creating deal:', error);
      return null;
    }
  }

  // Обновление сделки
  async updateDeal(dealId: string, dealData: Partial<DealDTO>): Promise<DealDTO> {
    try {
      const response = await api.put(`${API_ENDPOINTS.DEAL.LIST}/${dealId}`, dealData);
      return response.data as DealDTO;
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  }

  // Удаление сделки (только для статусов 'New' и 'Registered')
  async deleteDeal(dealId: string): Promise<boolean> {
    try {
      await api.delete(`${API_ENDPOINTS.DEAL.DELETE}/${dealId}/delete`);
      return true;
    } catch (error) {
      console.error('Error deleting deal:', error);
      return false;
    }
  }

  // Получение статистики по сделкам
  async getDealsStats(): Promise<{
    total: number;
    totalAmount: number;
    totalCommission: number;
    byStatus: Record<string, number>;
  }> {
    try {
      const deals = await this.getDeals();
      
      const stats = {
        total: deals.length,
        totalAmount: deals.reduce((sum, deal) => sum + deal.amount, 0),
        totalCommission: deals.reduce((sum, deal) => sum + deal.platformCommission, 0),
        byStatus: {} as Record<string, number>
      };

      // Группируем по статусам
      deals.forEach(deal => {
        stats.byStatus[deal.status] = (stats.byStatus[deal.status] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error calculating deals stats:', error);
      return {
        total: 0,
        totalAmount: 0,
        totalCommission: 0,
        byStatus: {}
      };
    }
  }

  // Проверка возможности удаления сделки
  canDeleteDeal(dealStatus: string): boolean {
    return dealStatus === 'New' || dealStatus === 'Registered';
  }

  // Форматирование суммы из копеек в рубли
  formatAmountFromKopeks(amountInKopeks: number): string {
    const amountInRubles = amountInKopeks / 100;
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2
    }).format(amountInRubles);
  }

  // Конвертация суммы из рублей в копейки
  convertRublesToKopeks(amountInRubles: number): number {
    return Math.round(amountInRubles * 100);
  }
}

export const dealService = new DealService();
export default dealService;
